import { Button, Header } from "@/presentation/components";
import { DataTable, DataTableColumn } from "@/presentation/components/common/DataTable";
import { AppDrawer } from "@/presentation/components/layout/AppDrawer/AppDrawer";
import { BottomAppBar } from "@/presentation/components/layout/BottomAppBar";
import { useAuthContext } from "@/presentation/context";
import { useIncidente } from "@/presentation/hooks/useIncidente";
import { routes } from "@/presentation/router";
import { formatPriority, formatState } from "@/shared";
import { formatSmartDateTime } from "@/shared/utils/dateFormaters";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateDialog } from "../action/components/CreateActionDialog";
import { Form } from "@/presentation/components/Forms/Form/Form";
import { useSimple } from "@/presentation/hooks/useSimple";
import { useCongrupo } from "@/presentation/hooks/useCongrupo";
import { IncidentesParamsDTO } from "@/application";
import { StatusBadge } from "@/presentation"
import './IncidentePage.css';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const {
    data: incidentes,
    loadAll,
    loading,
    error,
  } = useIncidente();



  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };
  const [openFilters, setOpenFilters] = useState(false)

  const handleLogout = async () => {
    await logout();
    setIsDrawerOpen(false);
    navigate(routes.login, { replace: true });
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: 'id',
      header: 'Nro',
    },
    {

      key: 'equipo',
      header: 'Equipo',
      render: (incidente) => (
        <button
          type="button"
          className="table-link"
          onClick={(event) => {
            event.stopPropagation();

            setParams((current) => ({
              ...current,
              equipo: incidente.equipo,
              pages: 1,
            }));
          }}
        >
          {incidente.equipo}
        </button>
      ),
    },
    {
      key: 'ubicacion',
      header: 'Ubicación',
    },
    {
      key: 'grupo',
      header: 'Grupo',
    },
    {
      key: 'estado',
      header: 'Estado',
      render: ({ estado }) => (
        <StatusBadge
          estado={estado}
          label={formatState(estado)}
        />
      ),
    },
    {
      key: 'prioridad',
      header: 'Prioridad',
      render: (incident) => formatPriority(incident.prioridad),
    },
    {
      key: 'tipo',
      header: 'Tipo',
    },
    {
      key: 'iniciado',
      header: 'Iniciado',
      render: (incident) =>
        formatSmartDateTime(incident.iniciado),
    },
    {
      key: 'responsable',
      header: 'Responsable',
      render: (incidente) =>
    Number(incidente.tipo_responsable) === 2
      ? `${incidente.responsable} - Externo`
      : incidente.responsable,
    },

  ];
  const {
    data: grupos,
    reload: loadGrupos,
  } = useSimple('luc_ubicaciones_grupo');
  const {
    data: tipos,
    reload: loadTipos,
  } = useSimple('luc_tipo_incidente');

  const {
    data: ubicaciones,
    loadByGroup,
  } = useCongrupo();

  const grupoOptions = useMemo(
    () =>
      grupos.map((grupo) => ({
        value: grupo.id,
        label: grupo.nombre,
      })),
    [grupos]
  );

  const ubicacionOptions = useMemo(
    () =>
      ubicaciones.map((ubicacion) => ({
        value: ubicacion.id,
        label: ubicacion.nombre,
      })),
    [ubicaciones]
  );
  const tipoOptions = useMemo(
    () =>
      tipos.map((tipo) => ({
        value: tipo.id,
        label: tipo.nombre,
      })),
    [tipos]
  );

  const nuevoincidente = () => {
    navigate('/incidentes/formulario/0', { replace: true });
  };

  const handleFilterClick = async () => {
    await loadGrupos()
    await loadTipos()
    setOpenFilters(true)
  };
  const [params, setParams] =
    useState<IncidentesParamsDTO>({
      pages: 1,
      sort: 'estado',
      order: 'asc',
    });

  useEffect(() => {
    loadAll(params);
  }, [params, loadAll]);

  const handleFilter = (
    values: IncidentesParamsDTO
  ) => {
    setParams((current) => ({
      ...current,
      ...values,
      pages: 1,
      equipo:
        values.equipo?.trim() || undefined,
    }));

    setOpenFilters(false);
  };
  const clearFilters = () => {
    setParams(
      {
        pages: 1,
        sort: 'estado',
        order: 'asc',
      });
  }
  const filterResponsable = () => {
    setParams(
      {
        responsable: user?.id ? Number(user.id) : undefined,
        pages: 1,
        sort: 'estado',
        order: 'asc',
      });
  }



  return (
    <div>
      <Header
        title="Incidentes"
        onMenuClick={handleOpenDrawer}
      />

      <AppDrawer
        isOpen={isDrawerOpen}
        user={user}
        onClose={handleCloseDrawer}
        onLogout={handleLogout}
      />
      <main className="incidentes-page">
        <div className="incidentes-page__toolbar">
          <Button
            onClick={clearFilters}
            disabled={loading}
          >
            Borrar Filtros
          </Button>
          <Button
            onClick={filterResponsable}
            disabled={loading}
          >
            Mis Asignaciones
          </Button>

          <Button
            onClick={handleFilterClick}
            disabled={loading}
          >
            Filtrar
          </Button>
        </div>

        {loading && (
          <p className="incidentes-page__message">
            Cargando incidentes...
          </p>
        )}

        {error && (
          <p className="incidentes-page__error">
            {error}
          </p>
        )}

        {!loading && !error && (
          <DataTable
            columns={columns}
            data={incidentes}
            onRowClick={(incidente) =>
              navigate(`/incidente/${incidente.id}`)
            }
            emptyMessage="No hay incidentes cargados"
            page={params.pages ?? 1}
            hasPrevious={(params.pages ?? 1) > 1}
            hasNext={incidentes.length === 25}
            onPrevious={() =>
              setParams((current) => ({
                ...current,
                pages: Math.max(
                  1,
                  (current.pages ?? 1) - 1
                ),
              }))
            }
            onNext={() =>
              setParams((current) => ({
                ...current,
                pages: (current.pages ?? 1) + 1,
              }))
            }
          />
        )}
      </main>

      <BottomAppBar
        items={[
          {
            label: 'Nuevo',
            onClick: nuevoincidente,
            icon: undefined,
          },
        ]}
      />
      <CreateDialog
        open={openFilters}
        title="Filtros"
        onClose={() => setOpenFilters(false)}
      >
        <Form
          fields={[
            {
              name: 'grupo',
              label: 'Grupo',
              type: 'select',
              options: grupoOptions,
              optional:true
            },
            {
              name: 'ubicacion',
              label: 'Ubicación',
              type: 'select',
              options: ubicacionOptions,
              optional:true
            },
            {
              name: 'tipo',
              label: 'Tipos de Incidentes',
              type: 'select',
              options: tipoOptions,
              optional:true
            },
            {
              name: 'estado',
              label: 'Estado',
              type: 'select',
              options: [
                {
                  "value": 1,
                  "label": "Pendiente"
                },
                {
                  "value": 2,
                  "label": "Logística"
                },
                {
                  "value": 3,
                  "label": "Espera de Repuesto"
                },
                {
                  "value": 4,
                  "label": "Remitido"
                },
                {
                  "value": 5,
                  "label": "En Destino"
                },
                {
                  "value": 6,
                  "label": "Asignado"
                },
                {
                  "value": 7,
                  "label": "En Curso"
                }
                ,
                {
                  "value": 8,
                  "label": "Cerrado"
                }
              ],
              optional:true

            },
            {
              name: "equipo",
              type: "text",
              label: "Equipo",
              optional:true
            },
            {
              name: "startDate",
              type: "date",
              label: "Desde",
              optional:true
            },
            {
              name: "endDate",
              type: "date",
              label: "Hasta",
              optional:true
            }

          ]}
          onFieldChange={(name, value) => {
            if (name === 'grupo') {
              loadByGroup(
                'luc_ubicaciones',
                String(value)
              );
            }
          }}
          submitLabel="Aceptar"
          onSubmit={handleFilter}
        />
      </CreateDialog>
    </div>

  );
}